import React from "react";
import { Input, Text } from "@ui-kitten/components";
import { StyleSheet, View } from "react-native";
import { Controller, useForm } from "react-hook-form";
import { AuthErrorCodes } from "firebase/auth";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import LoadingButton from "../components/LoadingButton";
import { AppNavigatorScreenProps } from "../navigators/AppNavigatorScreenProps";
import isErrorResponse from "../services/isErrorResponse";
import logError from "../helpers/logError";
import ScreenContainer from "../components/ScreenContainer";
import changePassword from "../services/changePassword";
import reauthenticate from "../services/reauthenticate";
import i18next from "../i18n/i18n";

type FormInput = {
    currentPassword: string;
    newPassword: string;
    repeatNewPassword: string;
};

const ChangePasswordScreen: React.FC<
    AppNavigatorScreenProps<"ChangePasswordScreen">
> = ({ navigation: { navigate } }) => {
    const {
        control,
        handleSubmit,
        setError,
        formState: { isValid, isDirty },
    } = useForm<FormInput>({
        mode: "onChange",
        reValidateMode: "onChange",
    });
    const [isLoading, setIsLoading] = React.useState(false);
    const { t } = useTranslation();

    const onSubmit = React.useCallback(
        (data: FormInput) => {
            if (data.newPassword !== data.repeatNewPassword) {
                setError("repeatNewPassword", {
                    type: "validate",
                    message: i18next.t("register.passwordsNotSame"),
                });
                return;
            }

            setIsLoading(true);
            reauthenticate(data.currentPassword)
                .then(() => {
                    if (data.currentPassword === data.newPassword) {
                        setError("newPassword", {
                            type: "validate",
                            message: i18next.t(
                                "changePassword.passwordIsSameAsLastOne",
                            ),
                        });
                        return;
                    }

                    changePassword(data.newPassword)
                        .then(() => {
                            toast.success(
                                i18next.t("changePassword.passwordChanged"),
                            );
                            navigate("HomeScreen");
                        })
                        .catch((error) => {
                            if (isErrorResponse(error)) {
                                switch (error.code) {
                                    case AuthErrorCodes.WEAK_PASSWORD:
                                        setError("newPassword", {
                                            type: "validate",
                                            message: i18next.t(
                                                "register.passwordTooShort",
                                            ),
                                        });
                                        break;
                                    default:
                                        logError(error);
                                        setError("newPassword", {
                                            type: "validate",
                                            message:
                                                i18next.t("errors.internal"),
                                        });
                                        break;
                                }
                            }
                        })
                        .finally(() => setIsLoading(false));
                })
                .catch((error) => {
                    if (isErrorResponse(error)) {
                        switch (error.code) {
                            case AuthErrorCodes.INVALID_PASSWORD:
                                setError("currentPassword", {
                                    type: "validate",
                                    message: i18next.t(
                                        "changePassword.invalidPassword",
                                    ),
                                });
                                break;
                            default:
                                logError(error);
                                setError("currentPassword", {
                                    type: "validate",
                                    message: i18next.t("errors.internal"),
                                });
                                break;
                        }
                    }
                })
                .finally(() => setIsLoading(false));
        },
        [setError, navigate],
    );
    return (
        <ScreenContainer contentContainerStyle={styles.container}>
            <View>
                <Text category="h3">
                    {t("changePassword.changeOwnPassword")}
                </Text>
                <Controller
                    control={control}
                    render={({
                        field: { onChange, onBlur, value },
                        formState: { errors },
                    }) => (
                        <View style={styles.inputMargin}>
                            <Input
                                label={t("changePassword.currentPassword")}
                                placeholder={t("labels.passwordExample")}
                                value={value}
                                secureTextEntry
                                status={
                                    errors.currentPassword?.type === "validate"
                                        ? "danger"
                                        : "basic"
                                }
                                onBlur={onBlur}
                                onChangeText={(nextValue) =>
                                    onChange(nextValue)
                                }
                            />
                            {errors.currentPassword?.type === "validate" && (
                                <View style={styles.errorMessage}>
                                    <Text
                                        style={styles.errorMessage}
                                        category="c1"
                                        status="danger"
                                    >
                                        {errors.currentPassword.message}
                                    </Text>
                                </View>
                            )}
                        </View>
                    )}
                    name="currentPassword"
                    defaultValue=""
                    rules={{ required: true }}
                />
                <Controller
                    control={control}
                    render={({
                        field: { onChange, onBlur, value },
                        formState: { errors },
                    }) => (
                        <View style={styles.inputMargin}>
                            <Input
                                label={t("changePassword.newPassword")}
                                placeholder={t("labels.passwordExample")}
                                value={value}
                                secureTextEntry
                                status={
                                    errors.newPassword?.type === "validate"
                                        ? "danger"
                                        : "basic"
                                }
                                onBlur={onBlur}
                                onChangeText={(nextValue) =>
                                    onChange(nextValue)
                                }
                            />
                            {errors.newPassword?.type === "validate" && (
                                <View style={styles.errorMessage}>
                                    <Text
                                        style={styles.errorMessage}
                                        category="c1"
                                        status="danger"
                                    >
                                        {errors.newPassword.message}
                                    </Text>
                                </View>
                            )}
                        </View>
                    )}
                    name="newPassword"
                    defaultValue=""
                    rules={{ required: true }}
                />
                <Controller
                    control={control}
                    render={({
                        field: { onChange, onBlur, value },
                        formState: { errors },
                    }) => (
                        <View style={styles.inputMargin}>
                            <Input
                                label={t("changePassword.configNewPassword")}
                                placeholder={t("labels.passwordExample")}
                                value={value}
                                secureTextEntry
                                status={
                                    errors.repeatNewPassword?.type ===
                                    "validate"
                                        ? "danger"
                                        : "basic"
                                }
                                onBlur={onBlur}
                                onChangeText={(nextValue) =>
                                    onChange(nextValue)
                                }
                            />
                            {errors.repeatNewPassword?.type === "validate" && (
                                <View style={styles.errorMessage}>
                                    <Text
                                        style={styles.errorMessage}
                                        category="c1"
                                        status="danger"
                                    >
                                        {errors.repeatNewPassword.message}
                                    </Text>
                                </View>
                            )}
                        </View>
                    )}
                    name="repeatNewPassword"
                    defaultValue=""
                    rules={{ required: true }}
                />
                <View style={styles.inputMargin}>
                    <LoadingButton
                        isLoading={isLoading}
                        disabled={!isValid || !isDirty}
                        onPress={handleSubmit(onSubmit)}
                    >
                        {t("labels.changePassword")}
                    </LoadingButton>
                </View>
            </View>
        </ScreenContainer>
    );
};

const styles = StyleSheet.create({
    container: {
        marginTop: 50,
        flex: 1,
        alignItems: "center",
    },
    inputMargin: {
        marginTop: 15,
    },
    errorMessage: {
        marginTop: 4,
    },
});

export default ChangePasswordScreen;
